import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { Search, X, Clock, MessageSquare, ChevronRight } from 'lucide-react';
import { Avatar, Pill } from '../components/UI';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { api, mapBackendStatus } from '../api';

type Scope = 'all' | 'users' | 'tickets' | 'messages';

const toValidDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const SearchScreen = () => {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [results, setResults] = useState<{ users: any[]; tickets: any[]; messages: any[] }>({ users: [], tickets: [], messages: [] });
  const { tickets, users, setActiveTicketId } = useStore();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;

  const scopes: { id: Scope; label: string }[] = [
    { id: 'all', label: 'Всё' },
    { id: 'users', label: 'Пользователи' },
    { id: 'tickets', label: 'Тикеты' },
    { id: 'messages', label: 'Сообщения' },
  ];

  useEffect(() => {
    if (!isSearching || q.length < 2) {
      setResults({ users: [], tickets: [], messages: [] });
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const response = await api.search(q, 10);
        setResults({
          users: scope === 'all' || scope === 'users' ? response.customers : [],
          tickets: scope === 'all' || scope === 'tickets' ? response.tickets : [],
          messages: scope === 'all' || scope === 'messages' ? response.messages : [],
        });
      } catch {
        if (!controller.signal.aborted) {
          setResults({ users: [], tickets: [], messages: [] });
        }
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [isSearching, q, scope]);

  const hasResults = results.users.length > 0 || results.tickets.length > 0 || results.messages.length > 0;
  const totalCount = results.users.length + results.tickets.length + results.messages.length;

  const openTicket = (ticketId: string) => {
    setActiveTicketId(ticketId);
    navigate('/');
  };

  const openUser = (userId: string) => {
    navigate('/users');
  };

  const highlight = (text: string, query: string) => {
    if (!query) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return <>{text}</>;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-[#FFCC00]/40 rounded-[2px] px-0.5">{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  const recentUsers = users.slice(0, 4);

  const statusLabel: Record<string, string> = { open: 'Открыт', pending: 'В работе', closed: 'Закрыт' };
  const statusType: Record<string, any> = { open: 'open', pending: 'pending', closed: 'closed' };

  return (
    <div className="flex-1 h-full bg-[#F5F5F7] flex flex-col mobile-content-safe overflow-hidden">
      {/* Search Header */}
      <div className="px-4 pt-5 pb-3 bg-[#F5F5F7]/90 backdrop-blur-2xl sticky top-0 z-10 border-b border-black/5 shrink-0">
        <div className="relative mb-4 max-w-4xl mx-auto w-full">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" strokeWidth={2} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Поиск по пользователям, тикетам..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full bg-white border border-black/5 shadow-sm outline-none rounded-[12px] py-3 pl-10 pr-10 text-[16px] focus:ring-2 focus:ring-[#007AFF]/40 transition-all placeholder:text-[#8E8E93]"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-[#C7C7CC] rounded-full flex items-center justify-center hover:bg-[#8E8E93] transition-colors"
            >
              <X size={12} className="text-white" strokeWidth={3} />
            </button>
          )}
        </div>

        {/* Scope Chips */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar max-w-4xl mx-auto w-full pb-1">
          {scopes.map(s => {
            let count = 0;
            if (isSearching) {
              if (s.id === 'all') count = totalCount;
              else if (s.id === 'users') count = results.users.length;
              else if (s.id === 'tickets') count = results.tickets.length;
              else if (s.id === 'messages') count = results.messages.length;
            }
            return (
              <button
                key={s.id}
                onClick={() => setScope(s.id)}
                className={`px-4 py-1.5 rounded-full text-[14px] font-medium whitespace-nowrap transition-all shadow-sm border flex items-center gap-1.5 ${
                  scope === s.id
                    ? 'bg-[#007AFF] text-white border-[#007AFF]'
                    : 'bg-white text-black border-black/5 hover:bg-[#F2F2F7]'
                }`}
              >
                {s.label}
                {isSearching && count > 0 && (
                  <span className={`text-[11px] font-bold px-1 h-[16px] min-w-[16px] rounded-full flex items-center justify-center ${
                    scope === s.id ? 'bg-white/30 text-white' : 'bg-[#007AFF]/10 text-[#007AFF]'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 max-w-4xl w-full mx-auto space-y-5">
        {/* Recent (not searching) */}
        {!isSearching && (
          <div className="space-y-4">
            <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest ml-1">Недавние</h3>
            <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden">
              {recentUsers.map((user, i) => (
                <button
                  key={user.id}
                  onClick={() => openUser(user.id)}
                  className={`w-full p-4 flex items-center gap-3.5 hover:bg-[#F9F9F9] transition-colors text-left ${i !== recentUsers.length - 1 ? 'border-b border-black/5' : ''}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center text-[#8E8E93] shrink-0">
                    <Clock size={17} strokeWidth={2} />
                  </div>
                  <Avatar name={`${user.firstName} ${user.lastName}`} size="42px" isOnline={user.isOnline} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-black">{user.firstName} {user.lastName}</div>
                    <div className="text-[13px] text-[#8E8E93] truncate">{user.username}</div>
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0" />
                </button>
              ))}
            </div>

            <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest ml-1 mt-5">Последние тикеты</h3>
            <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden">
              {tickets.slice(0, 3).map((ticket, i) => {
                const user = users.find(u => u.id === ticket.userId);
                return (
                  <button
                    key={ticket.id}
                    onClick={() => openTicket(ticket.id)}
                    className={`w-full p-4 flex items-center gap-3.5 hover:bg-[#F9F9F9] transition-colors text-left ${i !== 2 ? 'border-b border-black/5' : ''}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center text-[#8E8E93] shrink-0">
                      <MessageSquare size={17} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-medium text-black truncate">{ticket.subject}</div>
                      <div className="text-[13px] text-[#8E8E93] truncate">{user?.firstName} {user?.lastName}</div>
                    </div>
                    <Pill type={statusType[ticket.status]}>{statusLabel[ticket.status]}</Pill>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Results */}
        {isSearching && !hasResults && (
          <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden p-12 flex flex-col items-center justify-center text-center">
            <Search size={44} className="text-[#E5E5EA] mb-4" strokeWidth={1.5} />
            <div className="text-[17px] font-semibold text-black mb-1.5">Ничего не найдено</div>
            <div className="text-[15px] text-[#8E8E93]">Попробуйте изменить поисковый запрос</div>
          </div>
        )}

        {/* Users results */}
        {isSearching && results.users.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2 ml-1">
              <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Пользователи</h3>
              <span className="text-[12px] text-[#8E8E93]">{results.users.length}</span>
            </div>
            <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden">
            {results.users.map((user, i) => (
                <button
                  key={user.id}
                  onClick={() => openUser(user.id)}
                  className={`w-full p-4 flex items-center gap-3.5 hover:bg-[#F9F9F9] transition-colors text-left ${i !== results.users.length - 1 ? 'border-b border-black/5' : ''}`}
                >
                  <Avatar name={`${user.firstName} ${user.lastName}`} size="44px" isOnline={user.isOnline} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-black">
                      {highlight(`${user.firstName || ''} ${user.lastName || ''}`.trim(), query)}
                    </div>
                    <div className="text-[13px] text-[#8E8E93] truncate">{highlight(user.username ? `@${user.username}` : user.telegramId, query)}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[12px] text-[#8E8E93]">ID {user.telegramId}</div>
                    <ChevronRight size={18} className="text-[#C7C7CC] mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tickets results */}
        {isSearching && results.tickets.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2 ml-1">
              <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Тикеты</h3>
              <span className="text-[12px] text-[#8E8E93]">{results.tickets.length}</span>
            </div>
            <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden">
              {results.tickets.map((ticket, i) => {
                const linkedUser = users.find(u => u.id === ticket.customerId);
                const status = mapBackendStatus(ticket.status);
                return (
                  <button
                    key={ticket.id}
                    onClick={() => openTicket(ticket.id)}
                    className={`w-full p-4 flex items-center gap-3.5 hover:bg-[#F9F9F9] transition-colors text-left ${i !== results.tickets.length - 1 ? 'border-b border-black/5' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center shrink-0">
                      <MessageSquare size={18} className="text-[#007AFF]" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-black truncate">
                        {highlight(ticket.description || ticket.shortId || ticket.id, query)}
                      </div>
                      <div className="text-[13px] text-[#8E8E93] mt-0.5 truncate">
                        {linkedUser?.firstName} {linkedUser?.lastName} • {ticket.shortId}
                      </div>
                    </div>
                    <Pill type={statusType[status]}>{statusLabel[status]}</Pill>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages results */}
        {isSearching && results.messages.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2 ml-1">
              <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Сообщения</h3>
              <span className="text-[12px] text-[#8E8E93]">{results.messages.length}</span>
            </div>
            <div className="bg-white rounded-[16px] shadow-sm border border-black/5 overflow-hidden">
              {results.messages.map((item, i) => {
                const linkedTicket = tickets.find(t => t.id === item.ticketId);
                const user = users.find(u => u.id === linkedTicket?.userId);
                const timestamp = toValidDate(item.timestamp ?? item.createdAt);
                return (
                  <button
                    key={item.msgId ?? item.id}
                    onClick={() => openTicket(item.ticketId)}
                    className={`w-full p-4 flex items-start gap-3.5 hover:bg-[#F9F9F9] transition-colors text-left ${i !== results.messages.length - 1 ? 'border-b border-black/5' : ''}`}
                  >
                    <Avatar name={user ? `${user.firstName} ${user.lastName}` : 'U'} size="38px" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[13px] font-semibold text-[#007AFF] truncate">
                          {item.ticketSubject ?? linkedTicket?.subject ?? linkedTicket?.shortId ?? item.ticketId}
                        </span>
                        <span className="text-[11px] text-[#8E8E93] ml-2 shrink-0">
                          {timestamp ? format(timestamp, 'HH:mm') : '—'}
                        </span>
                      </div>
                      <div className="text-[14px] text-black leading-snug line-clamp-2">
                        {highlight(item.text ?? '', query)}
                      </div>
                      <div className="text-[12px] text-[#8E8E93] mt-0.5">
                        {item.senderType === 'CUSTOMER' || item.senderType === 'user'
                          ? (user?.firstName || 'Клиент')
                          : 'Оператор'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
